/**
 * Pure helpers for the global Activity Log (DEV-4823). Dates sent to the API are UTC;
 * custom-range days are UTC days so the filter, the export and its file name agree.
 */
import { escapeCsvCell, formatTimestampParts } from './common/common';

export type TimePeriod =
  | 'last7Days'
  | 'last30Days'
  | 'last3Months'
  | 'last6Months'
  | 'thisYear'
  | 'lastYear'
  | 'allTime'
  | 'custom';

export const TIME_PERIOD_OPTIONS: Array<{ value: TimePeriod; label: string }> = [
  { value: 'last7Days', label: 'Last 7 Days' },
  { value: 'last30Days', label: 'Last 30 Days' },
  { value: 'last3Months', label: 'Last 3 months' },
  { value: 'last6Months', label: 'Last 6 months' },
  { value: 'thisYear', label: 'This year' },
  { value: 'lastYear', label: 'Last year' },
  { value: 'allTime', label: 'All Time' },
  { value: 'custom', label: 'Custom range' },
];

export interface UtcRange {
  from: string | null;
  to: string | null;
}

const DAY_MS = 24 * 60 * 60 * 1000;

const daysAgo = (now: Date, days: number) => new Date(now.getTime() - days * DAY_MS);

const monthsAgo = (now: Date, months: number) => {
  const year = now.getUTCFullYear();
  const targetMonth = now.getUTCMonth() - months;
  // Day 0 of the following month is the last day of targetMonth; clamp so e.g. Aug 31 - 6 months is Feb 28/29.
  const lastDayOfTargetMonth = new Date(Date.UTC(year, targetMonth + 1, 0)).getUTCDate();

  return new Date(
    Date.UTC(
      year,
      targetMonth,
      Math.min(now.getUTCDate(), lastDayOfTargetMonth),
      now.getUTCHours(),
      now.getUTCMinutes(),
      now.getUTCSeconds(),
      now.getUTCMilliseconds(),
    ),
  );
};

const startOfUtcYear = (year: number) => new Date(Date.UTC(year, 0, 1));

export const resolveTimeRange = (
  period: TimePeriod,
  now: Date,
  custom?: { start: string | null; end: string | null },
): UtcRange => {
  const to = now.toISOString();
  const year = now.getUTCFullYear();

  switch (period) {
    case 'last7Days':
      return { from: daysAgo(now, 7).toISOString(), to };
    case 'last30Days':
      return { from: daysAgo(now, 30).toISOString(), to };
    case 'last3Months':
      return { from: monthsAgo(now, 3).toISOString(), to };
    case 'last6Months':
      return { from: monthsAgo(now, 6).toISOString(), to };
    case 'thisYear':
      return { from: startOfUtcYear(year).toISOString(), to };
    case 'lastYear':
      return {
        from: startOfUtcYear(year - 1).toISOString(),
        to: new Date(startOfUtcYear(year).getTime() - 1).toISOString(),
      };
    case 'custom':
      return {
        from: custom?.start ? `${custom.start}T00:00:00.000Z` : null,
        to: custom?.end ? `${custom.end}T23:59:59.999Z` : null,
      };
    case 'allTime':
    default:
      return { from: null, to: null };
  }
};

export const isValidCustomRange = (start: string | null, end: string | null): boolean =>
  !!start && !!end && start <= end;

export const formatTimestamp = (iso: string, timeZone?: string): string => {
  const { month, day, year, hour, minute, zone } = formatTimestampParts(iso, timeZone, 'short');
  return `${month} ${day}, ${year}, ${hour}:${minute} ${zone}`;
};

/**
 * Maps a date-picker change to a custom-range day. Returns `undefined` for an invalid (partially typed)
 * date so callers can ignore it; `null` means the picker was cleared.
 */
export const pickerDayOrSkip = (
  date: { isValid(): boolean; format(template: string): string } | null,
): string | null | undefined => {
  if (date === null) return null;
  return date.isValid() ? date.format('YYYY-MM-DD') : undefined;
};

export const NO_ORGANIZATION = '__no_organization__';
export const SYSTEM_ADMINISTRATOR = '__system__';

/** Maps a multi-select value to the API's `ids` + `include...` pair. Nothing selected = everything. */
export const selectionToFilter = (
  selected: string[],
  noneKey: string,
): { ids: string[] | null; includeNone: boolean } => {
  if (selected.length === 0) return { ids: null, includeNone: true };
  return { ids: selected.filter((id) => id !== noneKey), includeNone: selected.includes(noneKey) };
};

export const filterToSelection = (ids: string[] | null, includeNone: boolean, noneKey: string): string[] => {
  if (ids === null) return [];
  return includeNone ? [...ids, noneKey] : [...ids];
};

export interface ActivityLogExportRowLike {
  timestampUtc: string;
  administratorName?: string | null;
  administratorEmail?: string | null;
  organizationId?: string | null;
  organizationName?: string | null;
  action: string;
  actionLabel?: string | null;
  targetType?: string | null;
  targetId?: string | null;
  targetLabel?: string | null;
}

export const CSV_COLUMNS = [
  'timestamp_utc',
  'administrator_name',
  'administrator_email',
  'organization_id',
  'organization_name',
  'action',
  'action_label',
  'target_type',
  'target_id',
  'target_label',
] as const;

export const toCsvRows = (rows: ActivityLogExportRowLike[]): Array<Record<string, string>> =>
  rows.map((row) => ({
    timestamp_utc: row.timestampUtc,
    administrator_name: escapeCsvCell(row.administratorName),
    administrator_email: escapeCsvCell(row.administratorEmail),
    organization_id: escapeCsvCell(row.organizationId),
    organization_name: escapeCsvCell(row.organizationName),
    action: escapeCsvCell(row.action),
    action_label: escapeCsvCell(row.actionLabel),
    target_type: escapeCsvCell(row.targetType),
    target_id: escapeCsvCell(row.targetId),
    target_label: escapeCsvCell(row.targetLabel),
  }));

const utcDay = (iso: string) => iso.slice(0, 10);

export const exportFileName = (range: UtcRange, rows: ActivityLogExportRowLike[], now: Date): string => {
  const today = utcDay(now.toISOString());
  const oldest = rows.reduce<string | null>(
    (min, row) => (min === null || row.timestampUtc < min ? row.timestampUtc : min),
    null,
  );
  const from = range.from ? utcDay(range.from) : (oldest && utcDay(oldest)) || today;
  const to = range.to ? utcDay(range.to) : today;
  return `activity-log_${from}_${to}.csv`;
};
